import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

import { cn } from "@/lib/utils";

// Button - single source of truth across the product. Five variants, four
// sizes, all theme-aware via the design tokens in globals.css. No
// hardcoded hex values: dark mode flips automatically.
//
// Conventions:
//   - `default` = primary CTA (filled ink). Use sparingly - one per view.
//   - `outline` = secondary action that lives next to a primary.
//   - `secondary` = tertiary, low-emphasis action (e.g. "Cancel").
//   - `ghost`    = nav links / icon buttons / inline actions.
//   - `destructive` = irreversible / refund / delete.
//   - `link`     = text-only, inline-with-prose actions.
const buttonVariants = cva(
  [
    "group/button relative inline-flex shrink-0 items-center justify-center gap-1.5",
    "rounded-full border border-transparent bg-clip-padding font-medium whitespace-nowrap",
    "outline-none select-none",
    "transition-[transform,background-color,color,border-color,box-shadow,opacity] duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/30",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    "active:translate-y-px",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "border-border/70 bg-gradient-to-b from-foreground to-foreground/85 text-background",
          "hover:from-foreground/95 hover:to-foreground/80",
          "shadow-[0_1px_0_rgba(255,255,255,0.16)_inset,0_10px_24px_-16px_rgba(0,0,0,0.55)]",
          "dark:shadow-[0_1px_0_rgba(255,255,255,0.1)_inset,0_10px_24px_-16px_rgba(0,0,0,0.75)]",
        ].join(" "),
        outline: [
          "border-border bg-background text-foreground",
          "hover:border-brand-border-strong hover:bg-muted/40",
          "dark:hover:bg-muted/60",
        ].join(" "),
        secondary: [
          "bg-muted text-foreground",
          "hover:bg-accent",
        ].join(" "),
        ghost: [
          "text-foreground/80",
          "hover:bg-muted hover:text-foreground",
          "rounded-lg",
        ].join(" "),
        destructive: [
          "bg-destructive text-white",
          "hover:opacity-90",
          "shadow-[0_1px_0_rgba(255,255,255,0.12)_inset,0_1px_2px_rgba(0,0,0,0.18)]",
        ].join(" "),
        link: [
          "h-auto rounded-none border-none bg-transparent p-0",
          "text-foreground underline underline-offset-[3px] decoration-foreground/30",
          "hover:decoration-foreground",
          "active:translate-y-0",
        ].join(" "),
      },
      size: {
        default: "h-10 px-5 text-[13px]",
        xs: "h-7 px-2.5 text-[11.5px] [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 px-3.5 text-[12.5px] [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-11 px-7 text-[14px]",
        icon: "size-10",
        "icon-xs": "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-8",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
    compoundVariants: [
      // Icon-sized buttons keep the round footprint.
      { size: ["icon", "icon-xs", "icon-sm", "icon-lg"], class: "rounded-full px-0" },
      // Ghost looks better as a soft-rounded square at icon sizes.
      {
        variant: "ghost",
        size: ["icon", "icon-xs", "icon-sm", "icon-lg"],
        class: "rounded-lg",
      },
      // Link variant ignores all size padding.
      {
        variant: "link",
        size: ["default", "xs", "sm", "lg"],
        class: "h-auto px-0",
      },
    ],
  },
);

const Button = forwardRef<HTMLButtonElement, ButtonPrimitive.Props & VariantProps<typeof buttonVariants>>(
  function Button({ className, variant = "default", size = "default", ...props }, ref) {
    return (
      <ButtonPrimitive
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);

export { Button, buttonVariants };
